import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  createContext,
  useCallback,
  useState,
} from 'react';

import {
  IDocumentDTO,
  type IFolderDTO,
  type IPaginationMetaProps,
  type ISort,
} from '@gbs-monorepo-packages/common';

import { getListDocumentsByFolder } from '../services/documentsFolder';
import * as FolderService from '../services/folders';

export interface IDocumentContextData {
  documents: IDocumentDTO[];
  document: IDocumentDTO | null;
  subFolders: IFolderDTO[];
  folder: IFolderDTO | null;
  loadingDocuments: boolean;
  loadingDocumentsPagination: boolean;
  paginationMeta: IPaginationMetaProps | null;
  sortOrder: ISort | null;

  setLoadingDocuments: (loading: boolean) => void;
  setLoadingDocumentsPagination: (loading: boolean) => void;
  setDocuments: Dispatch<SetStateAction<IDocumentDTO[]>>;
  setSubFolders: (subFolders: IFolderDTO[]) => void;
  setPaginationMeta: (meta: IPaginationMetaProps) => void;
  setSortOrder: (sort: ISort | null) => void;
  setSelectedFolder: (folder: IFolderDTO) => void;
  handleSetDocument: (document: IDocumentDTO | null) => void;

  selectedDocumentLink: IDocumentDTO | null;
  handleSelectDocumentLink: (document: IDocumentDTO | null) => void;

  listDocuments: (params: {
    folderId: string;
    page: number;
    limit: number;
    filter: string;
    sort: string;
  }) => Promise<void>;

  handleSetSortOrder: (sort: ISort | null) => void;
}

interface IDocumentProps {
  children: ReactNode;
}

export const DocumentContext = createContext<IDocumentContextData>(
  {} as IDocumentContextData
);

export const DocumentProvider = ({ children }: IDocumentProps): JSX.Element => {
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [loadingDocumentsPagination, setLoadingDocumentsPagination] =
    useState(false);
  const [documents, setDocuments] = useState<IDocumentDTO[]>([]);
  const [document, setDocument] = useState<IDocumentDTO | null>(null);
  const [subFolders, setSubFolders] = useState<IFolderDTO[]>([]);
  const [paginationMeta, setPaginationMeta] =
    useState<IPaginationMetaProps | null>(null);
  const [sortOrder, setSortOrder] = useState<ISort | null>(null);

  const [folder, setFolder] = useState<IFolderDTO | null>(null);

  const [selectedDocumentLink, setSelectedDocumentLink] =
    useState<IDocumentDTO | null>(null);

  const setSelectedFolder = useCallback(
    (folder: IFolderDTO) => {
      setFolder(folder);
    },
    [setFolder]
  );

  const listDocuments = useCallback(
    async ({
      folderId,
      page,
      limit,
      filter,
      sort,
    }: {
      folderId: string;
      page: number;
      limit: number;
      filter: string;
      sort: string;
    }) => {
      setLoadingDocuments(true);
      try {
        const fetchedDocuments = await getListDocumentsByFolder({
          folderId: folderId ?? '',
          page,
          limit,
          filter: filter ?? '',
          sort,
        });

        const fetchFolderInformation = await FolderService.getListFolderById({
          id: Number(folderId),
        });

        setSelectedFolder(fetchFolderInformation.data);
        setDocuments(
          fetchedDocuments.data.filter((item) => item.type === 'document')
        );
        setSubFolders(
          fetchedDocuments.data.filter((item) => item.type === 'folder')
        );
        setPaginationMeta(fetchedDocuments.meta);
      } finally {
        setLoadingDocuments(false);
      }
    },
    [setSelectedFolder]
  );

  const handleSetSortOrder = useCallback(
    (sort: ISort | null) => {
      setSortOrder(sort);
    },
    [setSortOrder]
  );

  const handleSelectDocumentLink = useCallback(
    (document: IDocumentDTO | null) => {
      setSelectedDocumentLink(document);
    },
    [setSelectedDocumentLink]
  );

  const handleSetDocument = useCallback(
    (document: IDocumentDTO | null) => {
      setDocument(document);
    },
    [setDocument]
  );

  return (
    <DocumentContext.Provider
      value={{
        documents,
        document,
        subFolders,
        folder,
        loadingDocuments,
        loadingDocumentsPagination,
        paginationMeta,
        sortOrder,
        setLoadingDocuments,
        setLoadingDocumentsPagination,
        setDocuments,
        setSubFolders,
        setPaginationMeta,
        setSortOrder,
        setSelectedFolder,
        listDocuments,
        handleSetSortOrder,
        handleSelectDocumentLink,
        selectedDocumentLink,
        handleSetDocument,
      }}
    >
      {children}
    </DocumentContext.Provider>
  );
};
